import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { UnidadeConsumidora } from '../../types';
import { X, Zap, TrendingUp, CreditCard, Clock, CheckCircle2, History, FileText, AlertTriangle, Link as LinkIcon, Upload, Loader2, File, Paperclip, Trash2, Building2, Mail, FileWarning, Split, ArrowRightLeft, FileBarChart, Bell } from 'lucide-react';
import { formatDate } from '../../src/lib/utils';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { derivarStatusBoleto } from '../../src/lib/statusBoleto';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmModal from '../../src/components/ConfirmModal';
import UnidadeForm from './UnidadeForm';
import ConsumoGeracaoTab from './ConsumoGeracaoTab';
import HistoricoCobrancasTab from './HistoricoCobrancasTab';
import { dividaDoDonoAnterior } from '../../src/lib/trocaDonoUc';
import HistoricoUcTab from './HistoricoUcTab';
import TitularesUcSecao from './TitularesUcSecao';
import PagadorUcSecao from './PagadorUcSecao';
import { ucComCadastroTravado } from '../../src/lib/permissaoCadastro';
import RelatorioCreditosModal from './RelatorioCreditosModal';
import AvisosUcTab from './components/avisos/AvisosUcTab';
import AvisosUcFaixa from './components/avisos/AvisosUcFaixa';
import { useAvisosUc } from '../../src/hooks/useAvisosUc';
import { supabase } from '../../src/lib/supabase';
import { derivarStatusAceite, rotuloCanal, ROTULOS_STATUS } from '../../src/lib/boletoUnico/aceite';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import DataCancelamentoUc from './DataCancelamentoUc';
import { CONTAS_SICOOB, contaDaUc, NOME_CONTA_SICOOB, type ContaSicoob } from '../../src/lib/contaSicoob';
import { useContasSicoob } from '../../src/hooks/useContasSicoob';

interface UCManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade: UnidadeConsumidora | null;
    onSave?: () => void;
    onUcFieldsUpdate?: (fields: Partial<UnidadeConsumidora>) => void;
    /** Aba selecionada a cada abertura. Omitido, mantém a última aba usada (comportamento antigo). */
    abaInicial?: TabType;
}

export type TabType ='dados_unidade' | 'dados_cadastrais' | 'consumo_geracao' | 'faturamento_cobranca' | 'historico_faturas' | 'historico_uc' | 'avisos';

export const getStatusBadgeColors = (situacao: string | null) => {
    switch (situacao) {
        case 'Ativa':
        case 'Ativa-S/-Rateio':
            return 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20';
        case '1° Ciclo':
        case '2° Ciclo':
            return 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20';
        case 'Cancelada':
            return 'bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20';
        default:
            return 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
};

/**
 * Tom do status de aceite -> classes do badge. Fora do componente porque é tabela fixa, e
 * separado de `ROTULOS_STATUS` (lógica pura, sem Tailwind) de propósito.
 */
/** Opção escolhida no seletor de conta Sicoob — mesmas cores do antigo liga/desliga. */
const COR_CONTA_ESCOLHIDA: Record<ContaSicoob, string> = {
    associacao: 'bg-teal-500 text-white shadow-sm',
    gestao: 'bg-blue-600 text-white shadow-sm',
    hsolar: 'bg-amber-500 text-white shadow-sm',
};

const TOM_STATUS_ACEITE: Record<string, string> = {
    neutro: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    espera: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:border-sky-700',
    ok: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
    alerta: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700',
};

const UCManagementModal: React.FC<UCManagementModalProps> = ({ isOpen, onClose, unidade, onSave, onUcFieldsUpdate, abaInicial }) => {
    const { user, profile } = useAuth();
    const [activeTab, setActiveTab] = useState<TabType>('dados_unidade');
    const backdropMouseDownRef = useRef(false);
    // Avisos para o faturamento: faixa no topo + aba "Avisos" usam a mesma lista.
    const avisosUc = useAvisosUc(isOpen ? unidade?.id : null);

    // states
    const [negociacao, setNegociacao] = useState<any | null>(null);
    const [relatedUcs, setRelatedUcs] = useState<UnidadeConsumidora[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    // KPI Cards State
    const [kpiEmissoes, setKpiEmissoes] = useState(0);
    const [kpiTotalEstimado, setKpiTotalEstimado] = useState(0);
    const [kpiEconomia, setKpiEconomia] = useState(0);
    const [kpiEmAberto, setKpiEmAberto] = useState(0);
    const [kpiPago, setKpiPago] = useState(0);
    const [kpiVencido, setKpiVencido] = useState(0);
    // Faturas do dono anterior (troca de dono da UC): ficam fora dos números acima.
    const [kpiDonoAnterior, setKpiDonoAnterior] = useState<{ faturas: number; emAberto: number }>({ faturas: 0, emAberto: 0 });
    const [kpiParcelado, setKpiParcelado] = useState(0);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [contaSicoob, setContaSicoob] = useState<ContaSicoob>('associacao');
    const [savingContaSicoob, setSavingContaSicoob] = useState(false);
    // Recusa do banco ao trocar a conta (ex.: conta ainda não liberada). Precisa ficar
    // visível: é o motivo de a escolha ter voltado sozinha.
    const [erroContaSicoob, setErroContaSicoob] = useState<string | null>(null);
    const contasSicoob = useContasSicoob(isOpen);
    const [receberCobrancaEmail, setReceberCobrancaEmail] = useState<boolean>(false);
    const [savingEmailFlag, setSavingEmailFlag] = useState(false);
    // Exceção ao bloqueio de envio por espelho. Ligar exige motivo escrito — o banco
    // recusa a flag sem ele (uc_permite_envio_espelho_com_motivo), e sem motivo
    // ninguém saberia depois por que este cliente pode receber fatura sem código
    // de barras. Por isso o toggle abre um campo em vez de salvar no clique.
    const [permiteEnvioEspelho, setPermiteEnvioEspelho] = useState<boolean>(false);
    const [motivoEspelho, setMotivoEspelho] = useState<string | null>(null);
    const [espelhoEm, setEspelhoEm] = useState<string | null>(null);
    const [espelhoPorNome, setEspelhoPorNome] = useState<string | null>(null);
    const [editandoMotivoEspelho, setEditandoMotivoEspelho] = useState(false);
    const [rascunhoMotivoEspelho, setRascunhoMotivoEspelho] = useState('');
    const [savingEspelhoFlag, setSavingEspelhoFlag] = useState(false);
    const [boletoUnico, setBoletoUnico] = useState<boolean>(false);
    const [savingBoletoUnico, setSavingBoletoUnico] = useState(false);
    // Espelho do aceite (boleto_unico_aceites). Estado separado porque o toggle pode
    // criar um aceite manual e a tela precisa refletir isso sem recarregar a UC.
    const [aceiteEm, setAceiteEm] = useState<string | null>(null);
    const [aceiteCanal, setAceiteCanal] = useState<string | null>(null);
    // Recusa do trigger de liberação (sem aceite / débito automático). Precisa ficar
    // visível: é o motivo de o toggle ter voltado sozinho.
    const [erroLiberacao, setErroLiberacao] = useState<string | null>(null);
    // Liga o boleto único sem aceite prévio registra um aceite 'manual' em nome de quem
    // clicou (o Portal do Consumidor da #5575 não existe ainda). Isso atribui ao cliente
    // uma autorização que ele não deu diretamente na tela — exige confirmação explícita
    // de quem está clicando, não só um toggle silencioso.
    const [confirmandoAceiteManual, setConfirmandoAceiteManual] = useState(false);
    // Atestado de cancelamento do débito automático gravado nesta sessão. Serve só para a
    // tela parar de oferecer o botão e orientar a tentar de novo.
    const [atestadoRegistrado, setAtestadoRegistrado] = useState(false);
    const statusAceite = derivarStatusAceite({
        boleto_unico: boletoUnico,
        boleto_unico_aceite_em: aceiteEm,
        boleto_unico_aceite_canal: aceiteCanal,
    });
    // Default true espelha o default da coluna: UC nova paga a distribuidora sozinha.
    const [repasseAutomatico, setRepasseAutomatico] = useState<boolean>(true);
    const [savingRepasse, setSavingRepasse] = useState(false);
    const [docToDelete, setDocToDelete] = useState<{ name: string; url: string } | null>(null);
    const [deletingDoc, setDeletingDoc] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState<{ fieldName: 'url_termo' | 'url_simulacao'; label: string } | null>(null);
    const [deletingLink, setDeletingLink] = useState(false);
    const [relatorioCreditosAberto, setRelatorioCreditosAberto] = useState(false);

    React.useEffect(() => {
        if (!isOpen || !unidade) return;
        if (abaInicial) setActiveTab(abaInicial);
        setContaSicoob(contaDaUc(unidade));
        setErroContaSicoob(null);
        setReceberCobrancaEmail(unidade.receber_cobranca_email ?? false);
        setPermiteEnvioEspelho(unidade.permite_envio_espelho ?? false);
        setMotivoEspelho(unidade.permite_envio_espelho_motivo ?? null);
        setEspelhoEm(unidade.permite_envio_espelho_em ?? null);
        setEspelhoPorNome(unidade.permite_envio_espelho_por_nome ?? null);
        setEditandoMotivoEspelho(false);
        setRascunhoMotivoEspelho('');
        setBoletoUnico(unidade.boleto_unico ?? false);
        setAceiteEm(unidade.boleto_unico_aceite_em ?? null);
        setAceiteCanal(unidade.boleto_unico_aceite_canal ?? null);
        setErroLiberacao(null);
        setAtestadoRegistrado(false);
        setRepasseAutomatico(unidade.repasse_equatorial_automatico ?? true);
    }, [isOpen, unidade?.id]);

    React.useEffect(() => {
        if (!isOpen || !unidade) return;

        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch Negociacao
                if (unidade.negociacao_id) {
                    const { data: negData } = await supabase
                        .from('negociacoes')
                        .select('id, url_termo, url_simulacao, documentos_json')
                        .eq('id', unidade.negociacao_id)
                        .single();
                    if (negData) setNegociacao(negData);
                } else {
                    setNegociacao(null);
                }

                // Fetch Related UCs
                if (unidade.cpf_cnpj_cliente) {
                    const { data: ucsData } = await supabase
                        .from('unidades_consumidoras')
                        .select('*')
                        .eq('cpf_cnpj_cliente', unidade.cpf_cnpj_cliente)
                        .neq('id', unidade.id);
                    if (ucsData) setRelatedUcs(ucsData);
                } else {
                    setRelatedUcs([]);
                }

                // Fetch KPI data for this UC
                if (unidade.numero_uc) {
                    const { data: faturaData } = await supabase
                        .from('faturas')
                        .select('id, valor_boleto_vigor, valor_emissao_boleto_unico, economia, status_boleto_vigor, emitido_em, status_fatura, vencimento_boleto, data_vencimento, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh, dono_anterior_troca_id, cobranca_agrupada')
                        .neq('status_fatura', 'Excluído')
                        .in('uc_codigo_norm', ucCodigoKeys(unidade.numero_uc, unidade.outro_numero_uc));

                    if (faturaData) {
                        const agora = new Date();
                        // Troca de dono: o que é do dono anterior não conta como deste cliente.
                        const anteriores = faturaData.filter((f: any) => f.dono_anterior_troca_id);
                        const divida = dividaDoDonoAnterior(anteriores, agora);
                        setKpiDonoAnterior({ faturas: anteriores.length, emAberto: divida.valor });
                        const doDono = faturaData.filter((f: any) => !f.dono_anterior_troca_id);
                        setKpiEmissoes(doDono.filter((f: any) => f.emitido_em).length);
                        setKpiTotalEstimado(doDono.reduce((s: number, f: any) => s + (Number(f.valor_boleto_vigor) || 0), 0));
                        setKpiEconomia(doDono.reduce((s: number, f: any) => s + (Number(f.economia) || 0), 0));
                        // Status derivado, não o campo cru: fatura de "pular cálculo" fica
                        // com valor 0 e `status_boleto_vigor` vazio ou 'Em aberto' e era
                        // contada aqui como cobrança em aberto sem existir boleto nenhum.
                        const contarStatus = (status: string) =>
                            doDono.filter((f: any) => derivarStatusBoleto(f, agora) === status).length;

                        setKpiEmAberto(contarStatus('Em aberto'));
                        setKpiPago(contarStatus('Pago'));
                        setKpiVencido(contarStatus('Vencido'));
                        setKpiParcelado(contarStatus('Parcelado'));
                    }
                }
            } catch (err) {
                console.error("Error fetching modal data", err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [isOpen, unidade]);

    if (!isOpen || !unidade) return null;

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, fieldName: 'url_termo' | 'url_simulacao' | 'documentos_json') => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingDoc(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${unidade.id}_${Date.now()}.${fileExt}`;
            const filePath = `documentos_uc/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('clientes_docs')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: publicUrlData } = supabase.storage
                .from('clientes_docs')
                .getPublicUrl(filePath);

            const fileUrl = publicUrlData.publicUrl;

            // Prepare update
            let updatePayload: any = {};
            if (fieldName === 'documentos_json') {
                const existingDocs = unidade.documentos_json || [];
                const newDocItem = { name: file.name, url: fileUrl };
                updatePayload[fieldName] = [...existingDocs, newDocItem];
            } else {
                // Para url_termo e url_simulacao, a estrutura é um array de objetos
                const newDocItem = { url_pdf: fileUrl, data_criacao: new Date().toISOString() };
                updatePayload[fieldName] = [newDocItem];
            }

            const { error: updateError } = await supabase
                .from('unidades_consumidoras')
                .update(updatePayload)
                .eq('id', unidade.id);

            if (updateError) throw updateError;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'anexar_documento_uc', origem: 'acao' });

            // Propagate to parent so the updated list persists across modal close/reopen.
            if (onUcFieldsUpdate) {
                onUcFieldsUpdate({ [fieldName]: updatePayload[fieldName] } as Partial<UnidadeConsumidora>);
            }

        } catch (error) {
            console.error('Error uploading document:', error);
            alert('Falha ao fazer upload do documento.');
        } finally {
            setUploadingDoc(false);
        }
    };

    const getStoragePathFromUrl = (fileUrl: string) => {
        try {
            const path = new URL(fileUrl).pathname;
            const marker = '/object/public/';
            const markerIndex = path.indexOf(marker);

            if (markerIndex === -1) return null;

            const storagePath = path.slice(markerIndex + marker.length);
            const [bucketId, ...objectPathParts] = storagePath.split('/');

            if (!bucketId || objectPathParts.length === 0) return null;

            return {
                bucketId,
                objectPath: objectPathParts.join('/'),
            };
        } catch {
            return null;
        }
    };

    const handleDeleteDocument = async () => {
        if (!docToDelete || !unidade) return;

        setDeletingDoc(true);
        try {
            const storageInfo = getStoragePathFromUrl(docToDelete.url);
            if (storageInfo) {
                const { error: removeError } = await supabase.storage
                    .from(storageInfo.bucketId)
                    .remove([storageInfo.objectPath]);

                if (removeError) throw removeError;
            }

            const currentDocs = Array.isArray(unidade.documentos_json) ? unidade.documentos_json : [];
            const nextDocs = currentDocs.filter((doc: any) => !(doc?.url === docToDelete.url && doc?.name === docToDelete.name));

            const { error: updateError } = await supabase
                .from('unidades_consumidoras')
                .update({ documentos_json: nextDocs })
                .eq('id', unidade.id);

            if (updateError) throw updateError;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'remover_documento_uc', origem: 'acao' });

            // Propagate to parent so the updated list persists across modal close/reopen.
            if (onUcFieldsUpdate) {
                onUcFieldsUpdate({ documentos_json: nextDocs } as Partial<UnidadeConsumidora>);
            }
            setDocToDelete(null);
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Falha ao excluir o anexo.');
        } finally {
            setDeletingDoc(false);
        }
    };

    const handleDeleteLink = async () => {
        if (!linkToDelete || !unidade) return;
        setDeletingLink(true);
        try {
            const fieldValue = (unidade as any)[linkToDelete.fieldName];
            const fileUrl = Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0].url_pdf : null;
            if (fileUrl) {
                const storageInfo = getStoragePathFromUrl(fileUrl);
                if (storageInfo) {
                    await supabase.storage.from(storageInfo.bucketId).remove([storageInfo.objectPath]);
                }
            }

            const { error: updateError } = await supabase
                .from('unidades_consumidoras')
                .update({ [linkToDelete.fieldName]: [] })
                .eq('id', unidade.id);

            if (updateError) throw updateError;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'remover_link_uc', origem: 'acao' });

            if (onUcFieldsUpdate) {
                onUcFieldsUpdate({ [linkToDelete.fieldName]: [] } as Partial<UnidadeConsumidora>);
            }
            setLinkToDelete(null);
        } catch (error) {
            console.error('Error deleting link:', error);
            alert('Falha ao excluir o arquivo.');
        } finally {
            setDeletingLink(false);
        }
    };

    // Sem a lista (falha de leitura), a conta fica clicável e o banco decide.
    const contaLiberada = (conta: ContaSicoob) =>
        contasSicoob !== null && (contasSicoob.find(c => c.conta === conta)?.emissao_ativa ?? true);
    const contasEmConfiguracao = (contasSicoob || []).filter(c => !c.emissao_ativa).map(c => c.nome);

    const handleTrocarContaSicoob = async (conta: ContaSicoob) => {
        if (!unidade || conta === contaSicoob) return;
        const anterior = contaSicoob;
        setContaSicoob(conta);
        setErroContaSicoob(null);
        setSavingContaSicoob(true);
        try {
            // O banco recusa conta não liberada e mantém emitir_em_vigor_gestao em dia.
            const { data, error } = await supabase
                .from('unidades_consumidoras')
                .update({ conta_sicoob: conta })
                .eq('id', unidade.id)
                .select('conta_sicoob')
                .maybeSingle();
            if (error) throw error;
            if (!data) throw new Error('A troca não foi gravada. Você tem permissão para editar esta UC?');
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({ conta_sicoob: conta, emitir_em_vigor_gestao: conta === 'gestao' });
        } catch (err: any) {
            console.error('Erro ao salvar conta_sicoob:', err);
            setContaSicoob(anterior);
            setErroContaSicoob(err?.message || 'Não foi possível trocar a conta.');
        } finally {
            setSavingContaSicoob(false);
        }
    };

    const handleToggleEmailFlag = async (value: boolean) => {
        if (!unidade) return;
        setReceberCobrancaEmail(value);
        setSavingEmailFlag(true);
        try {
            await supabase
                .from('unidades_consumidoras')
                .update({ receber_cobranca_email: value })
                .eq('id', unidade.id);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({ receber_cobranca_email: value });
        } catch (err) {
            console.error('Erro ao salvar receber_cobranca_email:', err);
            setReceberCobrancaEmail(!value);
        } finally {
            setSavingEmailFlag(false);
        }
    };

    /**
     * Liberação do espelho. Ligar grava motivo + quem + quando; desligar limpa os três,
     * senão a UC ficaria com um motivo órfão sugerindo uma liberação que não vale mais.
     */
    const handleLiberarEspelho = async () => {
        if (!unidade) return;
        const motivo = rascunhoMotivoEspelho.trim();
        if (motivo.length < 5) {
            alert('Escreva o motivo da liberação — ele fica registrado na UC.');
            return;
        }
        setSavingEspelhoFlag(true);
        const agora = new Date().toISOString();
        const quem = profile?.full_name || user?.email || 'Sistema';
        try {
            const { error } = await supabase
                .from('unidades_consumidoras')
                .update({
                    permite_envio_espelho: true,
                    permite_envio_espelho_motivo: motivo,
                    permite_envio_espelho_em: agora,
                    permite_envio_espelho_por_nome: quem,
                })
                .eq('id', unidade.id);
            if (error) throw error;
            setPermiteEnvioEspelho(true);
            setMotivoEspelho(motivo);
            setEspelhoEm(agora);
            setEspelhoPorNome(quem);
            setEditandoMotivoEspelho(false);
            setRascunhoMotivoEspelho('');
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({
                permite_envio_espelho: true,
                permite_envio_espelho_motivo: motivo,
                permite_envio_espelho_em: agora,
                permite_envio_espelho_por_nome: quem,
            });
        } catch (err) {
            console.error('Erro ao salvar permite_envio_espelho:', err);
            alert('Não consegui salvar a liberação do espelho.');
        } finally {
            setSavingEspelhoFlag(false);
        }
    };

    const handleRevogarEspelho = async () => {
        if (!unidade) return;
        setSavingEspelhoFlag(true);
        try {
            const { error } = await supabase
                .from('unidades_consumidoras')
                .update({
                    permite_envio_espelho: false,
                    permite_envio_espelho_motivo: null,
                    permite_envio_espelho_em: null,
                    permite_envio_espelho_por_nome: null,
                })
                .eq('id', unidade.id);
            if (error) throw error;
            setPermiteEnvioEspelho(false);
            setMotivoEspelho(null);
            setEspelhoEm(null);
            setEspelhoPorNome(null);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({
                permite_envio_espelho: false,
                permite_envio_espelho_motivo: null,
                permite_envio_espelho_em: null,
                permite_envio_espelho_por_nome: null,
            });
        } catch (err) {
            console.error('Erro ao revogar permite_envio_espelho:', err);
            alert('Não consegui desligar a liberação do espelho.');
        } finally {
            setSavingEspelhoFlag(false);
        }
    };

    /**
     * Liga/desliga a cobrança unificada.
     *
     * LIGAR passa a exigir aceite: o banco recusa `boleto_unico = true` sem aceite ativo
     * (trigger `trg_guarda_liberacao_boleto_unico`). Para a operação de hoje não parar —
     * o Portal do Consumidor, que é onde o cliente aceita, ainda não existe (#5575) — o
     * próprio toggle registra um aceite `canal='manual'` em nome de quem clicou. A
     * diferença para antes é que agora fica o rastro de quem autorizou.
     *
     * DESLIGAR não revoga o aceite: sair da cobrança unificada é operação da Vigor, o
     * aceite é ato do cliente. A UC volta para "Aceito, aguardando liberação".
     */
    const handleToggleBoletoUnico = async (value: boolean) => {
        if (!unidade) return;
        // Ligar sem aceite prévio vai criar um aceite 'manual' em nome de quem clicou —
        // isso é registrar, em nome do cliente, algo que ele não confirmou nesta tela.
        // Pede confirmação explícita antes de gravar, em vez de fazer isso silenciosamente
        // por trás de um toggle.
        if (value && !aceiteEm) {
            setConfirmandoAceiteManual(true);
            return;
        }
        await executarToggleBoletoUnico(value);
    };

    const executarToggleBoletoUnico = async (value: boolean) => {
        if (!unidade) return;
        const anterior = boletoUnico;
        setBoletoUnico(value);
        setSavingBoletoUnico(true);
        setErroLiberacao(null);
        let aceiteCriadoId: number | null = null;
        try {
            if (value && !aceiteEm) {
                const { data, error } = await supabase
                    .from('boleto_unico_aceites')
                    .insert({ uc_id: unidade.id, canal: 'manual', registrado_por: user?.id })
                    .select('id, aceito_em, canal')
                    .single();
                if (error) throw error;
                aceiteCriadoId = data.id;
                setAceiteEm(data.aceito_em);
                setAceiteCanal(data.canal);
            }

            const { error } = await supabase
                .from('unidades_consumidoras')
                .update({ boleto_unico: value })
                .eq('id', unidade.id);
            if (error) throw error;

            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({ boleto_unico: value });
        } catch (err: any) {
            console.error('Erro ao salvar boleto_unico:', err);
            // O aceite manual só existe para viabilizar esta liberação. Se ela foi
            // recusada, deixá-lo gravado diria que o cliente aderiu quando não aderiu.
            if (aceiteCriadoId) {
                await supabase.from('boleto_unico_aceites').delete().eq('id', aceiteCriadoId);
                setAceiteEm(null);
                setAceiteCanal(null);
            }
            setBoletoUnico(anterior);
            // 'check_violation' (23514) é o código das duas mensagens que os triggers
            // `fn_guarda_liberacao_boleto_unico`/`fn_sync_...` escrevem em português, já
            // pensadas para quem não é dev. Qualquer outro código é erro que o Postgres
            // formatou sozinho (ex.: unique_violation em corrida de aceite) — cru demais
            // pra mostrar na tela, então cai no texto genérico.
            const mensagemConhecida = err?.code === '23514' && typeof err?.message === 'string'
                ? err.message
                : null;
            setErroLiberacao(mensagemConhecida || 'Não foi possível alterar o boleto único. Tente novamente ou avise o time técnico se persistir.');
        } finally {
            setSavingBoletoUnico(false);
        }
    };

    /**
     * Atestado de cancelamento do débito automático (política #5571, Opção 2).
     *
     * Só aparece quando a trava barrou por débito automático — não é ação de rotina, é a
     * saída para quem já confirmou com o cliente ou com a Equatorial que o débito foi
     * cancelado. A prova definitiva vem sozinha depois: quando a próxima fatura chegar sem
     * o marcador, o banco carimba `cancelamento_confirmado_em`. Atestado que nunca recebe
     * confirmação é sinal de que alguém carimbou sem conferir.
     *
     * Garante o aceite antes: sem aceite ativo não há onde gravar o atestado.
     */
    const registrarAtestadoCancelamento = async () => {
        if (!unidade) return;
        setSavingBoletoUnico(true);
        setErroLiberacao(null);
        try {
            let aceiteId: number | null = null;
            if (!aceiteEm) {
                const { data, error } = await supabase
                    .from('boleto_unico_aceites')
                    .insert({ uc_id: unidade.id, canal: 'manual', registrado_por: user?.id })
                    .select('id, aceito_em, canal')
                    .single();
                if (error) throw error;
                aceiteId = data.id;
                setAceiteEm(data.aceito_em);
                setAceiteCanal(data.canal);
            }

            const alvo = supabase
                .from('boleto_unico_aceites')
                .update({
                    cancelamento_atestado_em: new Date().toISOString(),
                    cancelamento_atestado_por: user?.id,
                })
                .is('revogado_em', null);
            const { error } = aceiteId
                ? await alvo.eq('id', aceiteId)
                : await alvo.eq('uc_id', unidade.id);
            if (error) throw error;

            setAtestadoRegistrado(true);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
        } catch (err: any) {
            console.error('Erro ao registrar atestado de cancelamento:', err);
            setErroLiberacao('Não foi possível registrar o atestado. Tente novamente ou avise o time técnico.');
        } finally {
            setSavingBoletoUnico(false);
        }
    };

    const handleToggleRepasseAutomatico = async (value: boolean) => {
        if (!unidade) return;
        setRepasseAutomatico(value);
        setSavingRepasse(true);
        try {
            await supabase
                .from('unidades_consumidoras')
                .update({ repasse_equatorial_automatico: value })
                .eq('id', unidade.id);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'alternar_configuracao_uc', origem: 'acao' });
            onUcFieldsUpdate?.({ repasse_equatorial_automatico: value });
        } catch (err) {
            console.error('Erro ao salvar repasse_equatorial_automatico:', err);
            setRepasseAutomatico(!value);
        } finally {
            setSavingRepasse(false);
        }
    };

    // Helper functions to merge docs correctly from arrays
    const getUrlPdf = (field: any) => {
        if (Array.isArray(field) && field.length > 0) {
            return field[0].url_pdf;
        }
        return null;
    };

    const mergedTermo = getUrlPdf(unidade.url_termo) || getUrlPdf(negociacao?.url_termo);
    const mergedSimulacao = getUrlPdf(unidade.url_simulacao) || getUrlPdf(negociacao?.url_simulacao);

    // Merge docs from the UC (source of truth via prop) and from the negociacao
    const mergedDocs = (() => {
        const ucDocs = Array.isArray(unidade.documentos_json) ? unidade.documentos_json : [];
        const negDocs = Array.isArray(negociacao?.documentos_json) ? negociacao.documentos_json : [];
        return [...negDocs, ...ucDocs];
    })();

    const renderTabContent = () => {
        return (
            <>
                <div className={activeTab === 'dados_unidade' ? 'animate-fade-in' : 'hidden'}>
                    <div className="p-6 space-y-8">
                        {/* First row: Basic Info and Address */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Unidade Consumidora (UC) Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Zap size={16} className="text-primary" />
                                    Unidade Consumidora (UC)
                                </h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nº UC</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.numero_uc || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Outro Nº UC</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.outro_numero_uc || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nº do Parceiro</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.num_parceiro || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="col-span-3 space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Titularidade da UC</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.titular_fatura || unidade.nome_cliente || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">CPF/CNPJ (Titular)</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.cpf_cnpj_titular || unidade.cpf_cnpj_cliente || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data de Nascimento</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.data_nascimento_titular || unidade.data_nascimento_cliente || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    {unidade.segmento && (
                                        <div className="col-span-3 space-y-1">
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Segmento</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={unidade.segmento}
                                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Endereço da Unidade Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                                    Endereço da Unidade
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2 space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Logradouro</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.logradouro || unidade.endereco || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bairro</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.bairro || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">CEP</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.cep || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cidade</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.cidade || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">UF</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={unidade.estado || ''}
                                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white font-medium"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Emissão de Boleto Config */}
                        <div className="bg-blue-50/60 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl p-5">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg text-blue-600 dark:text-blue-400">
                                        <Building2 size={16} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Emissão de Boleto</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Conta SICOOB usada para emitir boletos desta UC
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {savingContaSicoob && <Loader2 size={16} className="animate-spin text-gray-400" />}
                                    <div
                                        role="radiogroup"
                                        aria-label="Conta SICOOB de emissão"
                                        className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-0.5"
                                    >
                                        {CONTAS_SICOOB.map(conta => {
                                            const escolhida = conta === contaSicoob;
                                            const liberada = contaLiberada(conta);
                                            return (
                                                <button
                                                    key={conta}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={escolhida}
                                                    disabled={savingContaSicoob || (!liberada && !escolhida)}
                                                    onClick={() => handleTrocarContaSicoob(conta)}
                                                    title={liberada ? NOME_CONTA_SICOOB[conta] : `${NOME_CONTA_SICOOB[conta]} ainda não está liberada para emitir`}
                                                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors disabled:cursor-not-allowed ${escolhida
                                                        ? COR_CONTA_ESCOLHIDA[conta]
                                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/60 disabled:opacity-40 disabled:hover:bg-transparent'}`}
                                                >
                                                    {NOME_CONTA_SICOOB[conta].replace(/^Vigor /, '')}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            {erroContaSicoob && (
                                <p className="text-xs font-medium text-rose-600 dark:text-rose-400 mt-3 flex items-start gap-1.5">
                                    <AlertTriangle size={14} className="shrink-0 mt-px" />
                                    {erroContaSicoob}
                                </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                                O boleto sai na conta escolhida. A troca vale a partir da próxima emissão: boleto já emitido continua na conta em que saiu.
                                {contasEmConfiguracao.length > 0 && (
                                    <> {contasEmConfiguracao.join(' e ')} ainda {contasEmConfiguracao.length > 1 ? 'não estão liberadas' : 'não está liberada'} para emitir.</>
                                )}
                            </p>
                        </div>

                        {/* Receber via E-mail Config */}
                        <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl p-5 mt-4 transition-all duration-300 hover:shadow-sm">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary">
                                        <Mail size={16} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Envio de Fatura por E-mail</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Sinalizar se o cliente deseja receber as cobranças também por e-mail
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${receberCobrancaEmail ? 'bg-primary/20 text-primary border-primary/30 dark:bg-primary/30 dark:text-primary-light' : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'}`}>
                                        {receberCobrancaEmail ? 'Enviar por E-mail' : 'Não Enviar'}
                                    </span>
                                    {savingEmailFlag ? (
                                        <Loader2 size={18} className="animate-spin text-gray-400" />
                                    ) : (
                                        <button
                                            onClick={() => handleToggleEmailFlag(!receberCobrancaEmail)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${receberCobrancaEmail ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-700'}`}
                                            role="switch"
                                            aria-checked={receberCobrancaEmail}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${receberCobrancaEmail ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Envio do Espelho Config */}
                        <div className="bg-amber-50/60 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl p-5 mt-4 transition-all duration-300 hover:shadow-sm">
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400">
                                        <FileWarning size={16} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Envio do Espelho da Fatura</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Por padrão o envio fica travado quando só existe o espelho — ele não tem PIX nem código de barras. Ligue para clientes que pagam a distribuidora por fora (contrato público, débito em conta).
                                        </p>
                                        {permiteEnvioEspelho && motivoEspelho ? (
                                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                                {motivoEspelho}
                                                {espelhoEm ? ` · ${formatDate(espelhoEm)}` : ''}
                                                {espelhoPorNome ? ` · ${espelhoPorNome}` : ''}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${permiteEnvioEspelho ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700' : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'}`}>
                                        {permiteEnvioEspelho ? 'Liberado' : 'Bloqueado'}
                                    </span>
                                    {savingEspelhoFlag ? (
                                        <Loader2 size={18} className="animate-spin text-gray-400" />
                                    ) : (
                                        <button
                                            onClick={() => {
                                                if (permiteEnvioEspelho) { handleRevogarEspelho(); return; }
                                                setRascunhoMotivoEspelho('');
                                                setEditandoMotivoEspelho(true);
                                            }}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${permiteEnvioEspelho ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                                            role="switch"
                                            aria-checked={permiteEnvioEspelho}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${permiteEnvioEspelho ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {editandoMotivoEspelho ? (
                                <div className="mt-4 pt-4 border-t border-amber-100 dark:border-amber-900/30">
                                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Por que este cliente pode receber o espelho?
                                    </label>
                                    <textarea
                                        value={rascunhoMotivoEspelho}
                                        onChange={(e) => setRascunhoMotivoEspelho(e.target.value)}
                                        rows={2}
                                        autoFocus
                                        placeholder="Ex.: contrato de pregão — o município paga a conta pelo processo administrativo, não pelo código de barras."
                                        className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    />
                                    <div className="flex items-center justify-end gap-2 mt-2">
                                        <button
                                            onClick={() => { setEditandoMotivoEspelho(false); setRascunhoMotivoEspelho(''); }}
                                            className="px-3 py-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleLiberarEspelho}
                                            disabled={savingEspelhoFlag}
                                            className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
                                        >
                                            Liberar envio
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {/* Boleto Único Config */}
                        <div className="bg-amber-50/60 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl p-5 mt-4 transition-all duration-300 hover:shadow-sm">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400">
                                        <CreditCard size={16} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Boleto Único</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {ROTULOS_STATUS[statusAceite].ajuda}
                                        </p>
                                        {aceiteEm && (
                                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                                Aceite em {formatDate(aceiteEm)} · {rotuloCanal(aceiteCanal)}
                                            </p>
                                        )}
                                        {/* Desligar o toggle não apaga o aceite — só sai da cobrança
                                            unificada. Sem esta linha, "Aceito, aguardando liberação"
                                            depois de desligar parece dizer que falta ligar de novo,
                                            não que o desligamento foi só isso: desligar. */}
                                        {aceiteEm && !boletoUnico && (
                                            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                                                O aceite do cliente continua registrado — desligar aqui não revoga a autorização dele, só tira a UC do boleto único.
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${TOM_STATUS_ACEITE[ROTULOS_STATUS[statusAceite].tom]}`}>
                                        {ROTULOS_STATUS[statusAceite].texto}
                                    </span>
                                    {savingBoletoUnico ? (
                                        <Loader2 size={18} className="animate-spin text-gray-400" />
                                    ) : (
                                        <button
                                            onClick={() => handleToggleBoletoUnico(!boletoUnico)}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${boletoUnico ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                                            role="switch"
                                            aria-checked={boletoUnico}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${boletoUnico ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* O banco recusa a liberação sem aceite ou com débito automático na
                                Equatorial. Sem esta mensagem, o toggle voltaria sozinho e a
                                pessoa não saberia por quê. */}
                            {erroLiberacao && (
                                <div className="flex items-start gap-2 mt-3 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2" role="alert">
                                    <AlertTriangle size={14} className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-rose-700 dark:text-rose-300">{erroLiberacao}</p>
                                        {/* Saída para a trava de débito automático: quem já confirmou
                                            o cancelamento atesta aqui e tenta de novo. Só aparece
                                            nesse caso — atestar não é ação de rotina. */}
                                        {/débito automático/i.test(erroLiberacao) && (
                                            atestadoRegistrado ? (
                                                <p className="text-xs text-rose-700 dark:text-rose-300 mt-1.5 font-semibold">
                                                    Atestado registrado. Ligue o boleto único de novo para concluir.
                                                </p>
                                            ) : (
                                                <button
                                                    onClick={registrarAtestadoCancelamento}
                                                    disabled={savingBoletoUnico}
                                                    className="mt-1.5 text-xs font-semibold text-rose-700 dark:text-rose-300 underline underline-offset-2 hover:no-underline disabled:opacity-50"
                                                >
                                                    Confirmei com o cliente que o débito automático foi cancelado
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Repasse à Equatorial — só existe dentro do boleto único: sem ele,
                                o cliente paga a distribuidora direto e não há repasse a fazer. */}
                            {boletoUnico ? (
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-amber-100 dark:border-amber-900/30">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400">
                                            <ArrowRightLeft size={16} />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">Repasse à Equatorial</h4>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {repasseAutomatico
                                                    ? 'O VigorHub paga a conta da distribuidora assim que o cliente paga o boleto.'
                                                    : 'A parceira paga a distribuidora por fora. A baixa é feita à mão em Repasse Equatorial.'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${repasseAutomatico ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700' : 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700'}`}>
                                            {repasseAutomatico ? 'Automático' : 'Manual'}
                                        </span>
                                        {savingRepasse ? (
                                            <Loader2 size={18} className="animate-spin text-gray-400" />
                                        ) : (
                                            <button
                                                onClick={() => handleToggleRepasseAutomatico(!repasseAutomatico)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${repasseAutomatico ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                                                role="switch"
                                                aria-checked={repasseAutomatico}
                                                aria-label="Repasse automático à Equatorial"
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${repasseAutomatico ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <hr className="border-gray-200 dark:border-gray-800" />

                        {/* Second row: Documents and Links */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Links Importantes */}
                            <div className="space-y-4 bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                <h3 className="text-sm font-bold text-blue-900 dark:text-blue-400 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <LinkIcon size={16} />
                                        Links Importantes
                                    </span>
                                </h3>
                                <div className="space-y-3">
                                    {/* Termo de Adesão */}
                                    <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                                <FileText size={16} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Termo de Adesão</p>
                                                <p className="text-[10px] text-gray-500">
                                                    {mergedTermo ? 'Visualizar ou baixar arquivo' : 'Nenhum termo disponível'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {mergedTermo && (
                                                <a href={mergedTermo} target="_blank" rel="noreferrer" className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                                                    Ver Link
                                                </a>
                                            )}
                                            {getUrlPdf(unidade.url_termo) && (
                                                <button
                                                    onClick={() => setLinkToDelete({ fieldName: 'url_termo', label: 'Termo de Adesão' })}
                                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    title="Excluir termo"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    id="upload-termo"
                                                    className="hidden"
                                                    onChange={(e) => handleFileUpload(e, 'url_termo')}
                                                    disabled={uploadingDoc}
                                                />
                                                <label htmlFor="upload-termo" className="cursor-pointer p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors inline-block">
                                                    <Upload size={18} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Simulação */}
                                    <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                                <TrendingUp size={16} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Simulação</p>
                                                <p className="text-[10px] text-gray-500">
                                                    {mergedSimulacao ? 'Visualizar ou baixar arquivo' : 'Nenhuma simulação disponível'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {mergedSimulacao && (
                                                <a href={mergedSimulacao} target="_blank" rel="noreferrer" className="p-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors">
                                                    Ver Link
                                                </a>
                                            )}
                                            {getUrlPdf(unidade.url_simulacao) && (
                                                <button
                                                    onClick={() => setLinkToDelete({ fieldName: 'url_simulacao', label: 'Simulação' })}
                                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    title="Excluir simulação"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    id="upload-simulacao"
                                                    className="hidden"
                                                    onChange={(e) => handleFileUpload(e, 'url_simulacao')}
                                                    disabled={uploadingDoc}
                                                />
                                                <label htmlFor="upload-simulacao" className="cursor-pointer p-2 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors inline-block">
                                                    <Upload size={18} />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Documentos Anexos */}
                            <div className="space-y-4 bg-gray-50 dark:bg-gray-800/20 p-5 rounded-xl border border-gray-200 dark:border-gray-700/50">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <Paperclip size={16} className="text-gray-500" />
                                        Documentos Anexos
                                    </span>
                                    <div className="relative">
                                        <input
                                            type="file"
                                            id="upload-doc"
                                            className="hidden"
                                            onChange={(e) => handleFileUpload(e, 'documentos_json')}
                                            disabled={uploadingDoc}
                                        />
                                        <label htmlFor="upload-doc" className="cursor-pointer text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2">
                                            {uploadingDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                            {uploadingDoc ? 'Enviando...' : 'Adicionar'}
                                        </label>
                                    </div>
                                </h3>

                                {mergedDocs.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {mergedDocs.map((doc: any, i: number) => (
                                            <div key={`${doc.url}-${i}`} className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-primary transition-colors group">
                                                <a href={doc.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 overflow-hidden flex-1 min-w-0">
                                                    <div className="p-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 group-hover:text-primary transition-colors">
                                                        <File size={14} />
                                                    </div>
                                                    <div className="overflow-hidden min-w-0">
                                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate" title={doc.name}>
                                                            {doc.name}
                                                        </p>
                                                    </div>
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => setDocToDelete({ name: doc.name, url: doc.url })}
                                                    className="p-1.5 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                                                    aria-label={`Excluir anexo ${doc.name}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                                        <p className="text-sm">Nenhum documento anexado.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Third Row: UCs of same client */}
                        {unidade.cpf_cnpj_cliente && (
                            <div className="space-y-4 pt-4">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Zap size={16} className="text-amber-500" />
                                    Outras UCs de {unidade.nome_cliente || 'esse cliente'} ({relatedUcs.length})
                                </h3>
                                {loadingData ? (
                                    <div className="flex justify-center p-4">
                                        <LoadingSpinner size="sm" />
                                    </div>
                                ) : relatedUcs.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {relatedUcs.map(uc => (
                                            <div key={uc.id} className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 rounded-lg">
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900 dark:text-white">{uc.numero_uc}</p>
                                                    <p className="text-xs text-gray-500">{uc.cidade}/{uc.estado}</p>
                                                </div>
                                                <span className="inline-flex flex-col items-end gap-0.5">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border ${getStatusBadgeColors(uc.situacao)}`}>
                                                        {uc.situacao || 'Desconhecido'}
                                                    </span>
                                                    <DataCancelamentoUc uc={uc} />
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                                        Não encontramos outras UCs para o CPF/CNPJ {unidade.cpf_cnpj_cliente}.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={activeTab === 'dados_cadastrais' ? '' : 'hidden'}>
                    <div className="py-2">
                        <PagadorUcSecao ucId={Number(unidade.id)} nomeCliente={unidade.nome_cliente} cpfCliente={unidade.cpf_cnpj_cliente} exigeMotivo={ucComCadastroTravado(unidade)} />
                        <UnidadeForm
                            isOpen={true}
                            onClose={onClose}
                            unidade={unidade}
                            onSave={() => {
                                if (onSave) onSave();
                            }}
                        />
                    </div>
                </div>

                <div className={activeTab === 'consumo_geracao' ? '' : 'hidden'}>
                    <ConsumoGeracaoTab
                        unidadeId={unidade.id.toString()}
                        numeroUc={unidade.numero_uc}
                        outroNumeroUc={unidade.outro_numero_uc}
                    />
                </div>

                <div className={activeTab === 'historico_faturas' ? '' : 'hidden'}>
                    <HistoricoCobrancasTab
                        unidadeId={unidade.id.toString()}
                        numeroUc={unidade.numero_uc}
                        outroNumeroUc={unidade.outro_numero_uc}
                    />
                </div>

                <div className={activeTab === 'avisos' ? '' : 'hidden'}>
                    <AvisosUcTab
                        uc={unidade}
                        avisos={avisosUc.avisos}
                        mesAtual={avisosUc.mesAtual}
                        carregando={avisosUc.carregando}
                        erro={avisosUc.erro}
                        recarregar={avisosUc.recarregar}
                    />
                </div>

                <div className={activeTab === 'historico_uc' ? '' : 'hidden'}>
                    <TitularesUcSecao
                        ucId={Number(unidade.id)}
                        numeroUc={unidade.numero_uc}
                        outroNumeroUc={unidade.outro_numero_uc}
                        ativo={activeTab === 'historico_uc'}
                    />
                    <HistoricoUcTab
                        unidadeId={unidade.id.toString()}
                        numeroUc={unidade.numero_uc}
                        outroNumeroUc={unidade.outro_numero_uc}
                    />
                </div>
            </>
        );
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
        >
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col m-4 border border-gray-200 dark:border-gray-800" onClick={e => e.stopPropagation()}>
                {/* Header Container */}
                <div className="flex-shrink-0 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-t-2xl">
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white">Detalhes da Unidade Consumidora</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Documento que o Financeiro manda ao associado quando ele questiona
                                    o saldo de créditos. Abre a conferência, não gera direto: parte das
                                    UCs tem mês sem informação de créditos e o total sairia menor. */}
                                <button
                                    onClick={() => setRelatorioCreditosAberto(true)}
                                    title="Gerar o relatório de créditos de energia desta unidade"
                                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-primary border border-primary/30 hover:bg-primary/10 rounded-lg transition-colors"
                                >
                                    <FileBarChart size={16} /> Relatório de Créditos
                                </button>
                                <button
                                    onClick={onClose}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 text-sm mb-6">
                            <span className="text-gray-500 font-medium">ID: <span className="text-gray-900 dark:text-white">{unidade.id}</span></span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getStatusBadgeColors(unidade.situacao)}`}>
                                                                {unidade.situacao || 'Indefinido'}
                                                            </span>
                            <DataCancelamentoUc uc={unidade} className="-ml-2" />
                            <span className="text-gray-300 dark:text-gray-700">|</span>
                            <span className="text-gray-500 font-medium">Nº UC: <span className="text-gray-900 dark:text-white">{unidade.numero_uc}</span></span>
                            {unidade.outro_numero_uc && (
                                <>
                                    <span className="text-gray-300 dark:text-gray-700">|</span>
                                    <span className="text-gray-500 font-medium">Outro Nº UC: <span className="text-gray-900 dark:text-white">{unidade.outro_numero_uc}</span></span>
                                </>
                            )}
                            <span className="text-gray-300 dark:text-gray-700">|</span>
                            <span className="text-gray-500 font-medium">Associado: <span className="text-gray-900 dark:text-white">{unidade.nome_cliente}</span></span>
                        </div>

                        {/* KPI Cards */}
                        <div className={`grid gap-4 ${kpiParcelado > 0 ? 'grid-cols-7' : 'grid-cols-6'}`}>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Emissões</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">{kpiEmissoes}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Total Estimado</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">R$ {kpiTotalEstimado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-emerald-200 dark:border-emerald-800/50 p-3 shadow-sm flex flex-col justify-center bg-emerald-50/30 dark:bg-emerald-900/10">
                                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">Economia Gerada</p>
                                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">R$ {kpiEconomia.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Em Aberto</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">{kpiEmAberto}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Pago</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">{kpiPago}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800/50 p-3 shadow-sm flex flex-col justify-center bg-red-50/30 dark:bg-red-900/10">
                                <p className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">Vencido</p>
                                <p className="text-xl font-bold text-red-600 dark:text-red-400">{kpiVencido}</p>
                            </div>
                            {kpiParcelado > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-indigo-200 dark:border-indigo-800/50 p-3 shadow-sm flex flex-col justify-center bg-indigo-50/30 dark:bg-indigo-900/10">
                                    <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Split size={9} /> Parcelado</p>
                                    <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{kpiParcelado}</p>
                                </div>
                            )}
                        </div>
                        {kpiDonoAnterior.faturas > 0 && (
                            <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                                Fora destes números: {kpiDonoAnterior.faturas} {kpiDonoAnterior.faturas === 1 ? 'fatura' : 'faturas'} do dono anterior (troca de dono da UC)
                                {kpiDonoAnterior.emAberto > 0 ? `, com R$ ${kpiDonoAnterior.emAberto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} em aberto no nome dele` : ''}.
                            </p>
                        )}
                        <AvisosUcFaixa ativos={avisosUc.ativos} onVer={() => setActiveTab('avisos')} />
                    </div>

                    {/* Tabs */}
                    <div className="px-6 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-white/5">
                        <div className="flex space-x-2 overflow-x-auto scroller -mb-px">
                            <button
                                onClick={() => setActiveTab('dados_unidade')}
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'dados_unidade'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <Zap size={16} /> Dados da Unidade
                            </button>
                            <button
                                onClick={() => setActiveTab('dados_cadastrais')}
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'dados_cadastrais'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                Dados Cadastrais
                            </button>
                            <button
                                onClick={() => setActiveTab('consumo_geracao')}
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'consumo_geracao'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <TrendingUp size={16} /> Consumo e Geração
                            </button>
                            <button
                                onClick={() => setActiveTab('historico_faturas')}
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'historico_faturas'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <History size={16} /> Histórico de cobranças
                            </button>
                            <button
                                onClick={() => setActiveTab('historico_uc')}
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'historico_uc'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <FileText size={16} /> Histórico da UC
                            </button>
                            <button
                                onClick={() => setActiveTab('avisos')}
                                data-aba="avisos"
                                className={`flex items-center gap-2 py-2.5 px-4 text-sm font-semibold border-t border-x rounded-t-lg transition-all duration-200 whitespace-nowrap ${activeTab === 'avisos'
                                    ? 'border-primary border-b-transparent bg-white dark:bg-gray-900 text-primary'
                                    : 'border-transparent border-b-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/30'
                                    }`}
                            >
                                <Bell size={16} /> Avisos
                                {avisosUc.ativos.length > 0 && (
                                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center">
                                        {avisosUc.ativos.length}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Container */}
                <div className="flex-1 overflow-y-auto w-full">
                    <div className="mx-auto w-full">
                        {renderTabContent()}
                    </div>
                </div>
                <ConfirmModal
                    isOpen={!!docToDelete}
                    onClose={() => !deletingDoc && setDocToDelete(null)}
                    onConfirm={handleDeleteDocument}
                    title="Excluir anexo"
                    message={`Tem certeza que deseja excluir o anexo ${docToDelete?.name || ''}?`}
                    confirmLabel="Excluir"
                    cancelLabel="Cancelar"
                    variant="danger"
                    loading={deletingDoc}
                />
                <ConfirmModal
                    isOpen={!!linkToDelete}
                    onClose={() => !deletingLink && setLinkToDelete(null)}
                    onConfirm={handleDeleteLink}
                    title={`Excluir ${linkToDelete?.label || ''}`}
                    message={`Tem certeza que deseja excluir o arquivo de ${linkToDelete?.label || ''}?`}
                    confirmLabel="Excluir"
                    cancelLabel="Cancelar"
                    variant="danger"
                    loading={deletingLink}
                />
                <RelatorioCreditosModal
                    isOpen={relatorioCreditosAberto}
                    onClose={() => setRelatorioCreditosAberto(false)}
                    unidade={unidade}
                />
                <ConfirmModal
                    isOpen={confirmandoAceiteManual}
                    onClose={() => !savingBoletoUnico && setConfirmandoAceiteManual(false)}
                    onConfirm={() => {
                        setConfirmandoAceiteManual(false);
                        executarToggleBoletoUnico(true);
                    }}
                    title="Registrar aceite em nome do cliente"
                    message="Esta UC ainda não tem aceite ao boleto único. Ao confirmar, o sistema vai registrar um aceite manual em seu nome, como se você tivesse coletado a autorização do cliente para unificar a cobrança da Vigor com a da Equatorial. Só confirme se o cliente já concordou por outro canal (telefone, WhatsApp, presencial)."
                    confirmLabel="Confirmar aceite e liberar"
                    cancelLabel="Cancelar"
                    variant="warning"
                    loading={savingBoletoUnico}
                />
            </div>
        </div>,
        document.body
    );
};

export default UCManagementModal;
